import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Trophy } from 'lucide-react';
import { supabase } from '../lib/supabase';
import SeoMeta from '../components/SeoMeta';

interface JuryComment {
  juror_name: string;
  comment: string;
}

interface Evaluation {
  id: string;
  category_name: string;
  category_type: 'project' | 'personal' | 'benchmark';
  status: string;
  candidate_name?: string | null;
  project_title?: string | null;
  average_score: number | null;
  comments: JuryComment[] | null;
}

interface NominationLite {
  category_name: string;
  category_type: 'project' | 'personal' | 'benchmark';
  status: string;
}

const TYPE_DOT: Record<string, string> = {
  project: '#ffd702',
  personal: '#7fd1ff',
  benchmark: '#b98bff',
};
const TYPE_ORDER: Record<string, number> = { project: 0, personal: 1, benchmark: 2 };
const isLatam = (name: string) => name.toUpperCase().includes('LATAM');

const titleOf = (item: Evaluation) =>
  item.category_type === 'personal'
    ? item.candidate_name || 'Candidatura sin nombre'
    : item.project_title || 'Candidatura sin título';

const ValoracionJurado: React.FC = () => {
  const [evaluations, setEvaluations] = useState<Evaluation[] | null>(null);
  const [nominations, setNominations] = useState<NominationLite[] | null>(null);
  const [error, setError] = useState(false);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [itemIndex, setItemIndex] = useState(0);
  const [commentIndex, setCommentIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [evalRes, nomsRes] = await Promise.all([
        supabase.schema('premios').rpc('get_public_jury_evaluations'),
        supabase.schema('premios').rpc('get_public_nominations'),
      ]);
      if (cancelled) return;
      if (evalRes.error || nomsRes.error || !evalRes.data || !nomsRes.data) {
        console.error('Error cargando valoración del jurado:', evalRes.error || nomsRes.error);
        setError(true);
        return;
      }
      setEvaluations(evalRes.data as Evaluation[]);
      setNominations(nomsRes.data as NominationLite[]);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!activeCategory) return;
    const items = byCategory(evaluations)[activeCategory] || [];
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setItemIndex((i) => Math.min(items.length - 1, i + 1));
      else if (e.key === 'ArrowLeft') setItemIndex((i) => Math.max(0, i - 1));
      else if (e.key === 'Escape') setActiveCategory(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, evaluations]);

  const byCategory = (evals: Evaluation[] | null) => {
    const map: Record<string, Evaluation[]> = {};
    (evals || []).forEach((e) => {
      map[e.category_name] = map[e.category_name] || [];
      map[e.category_name].push(e);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => (b.average_score || 0) - (a.average_score || 0))
    );
    return map;
  };

  // Al cambiar de candidatura, volvemos siempre al primer comentario.
  useEffect(() => {
    setCommentIndex(0);
  }, [itemIndex, activeCategory]);

  const openCategory = (name: string) => {
    setActiveCategory(name);
    setItemIndex(0);
  };

  if (error) {
    return (
      <div className="informe-page">
        <div className="informe-page-content" style={{ textAlign: 'center', paddingTop: 80 }}>
          <p>No se ha podido cargar la valoración del jurado. Inténtalo de nuevo en unos instantes.</p>
          <Link to="/candidaturas" className="wrapped-btn primary" style={{ marginTop: 16 }}>
            Volver
          </Link>
        </div>
      </div>
    );
  }

  if (!evaluations || !nominations) {
    return (
      <div className="informe-page">
        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}>
          <div className="wrapped-spinner" />
        </div>
      </div>
    );
  }

  const grouped = byCategory(evaluations);

  // El listado de categorías se construye a partir de TODAS las candidaturas
  // aprobadas (igual que en /informe-candidaturas), no solo las que ya tienen
  // votos del jurado, para que no "desaparezcan" categorías sin evaluar aún.
  const categoriesFromNominations: Record<string, { type: Evaluation['category_type']; count: number }> = {};
  nominations
    .filter((n) => n.status === 'approved' && n.category_type !== 'benchmark')
    .forEach((n) => {
      const entry = categoriesFromNominations[n.category_name] || { type: n.category_type, count: 0 };
      entry.count += 1;
      categoriesFromNominations[n.category_name] = entry;
    });

  const categoryStats = Object.keys(categoriesFromNominations)
    .map((name) => ({
      name,
      type: categoriesFromNominations[name].type,
      count: categoriesFromNominations[name].count,
    }))
    .sort((a, b) => {
      if (TYPE_ORDER[a.type] !== TYPE_ORDER[b.type]) return TYPE_ORDER[a.type] - TYPE_ORDER[b.type];
      const la = isLatam(a.name);
      const lb = isLatam(b.name);
      if (la !== lb) return la ? 1 : -1;
      return b.count - a.count;
    });
  const catGridRows = Math.ceil(categoryStats.length / 2);

  /* -------------------------------------------------- Visor de una categoría */
  if (activeCategory) {
    const items = grouped[activeCategory] || [];
    const item = items[itemIndex];
    const maxScore = items.reduce((m, r) => Math.max(m, r.average_score || 0), 0) || 1;

    return (
      <div className="informe-page">
        <SeoMeta
          title={`${activeCategory} · Valoración del jurado · Premios NovaGob Innovación Pública`}
          description={`Puntuaciones y comentarios del jurado experto sobre las candidaturas de la categoría "${activeCategory}" en los Premios NovaGob Excelencia a la innovación pública.`}
          path="/valoracion-jurado"
        />
        <header className="informe-page-header">
          <button type="button" className="informe-page-back" onClick={() => setActiveCategory(null)}>
            <ArrowLeft size={18} /> Categorías
          </button>
          <span className="informe-page-title">
            {activeCategory} · {itemIndex + 1} / {items.length}
          </span>
          <Link to="/" className="informe-page-home">
            Volver al inicio
          </Link>
        </header>

        <div className="informe-page-content" style={{ maxWidth: 1040 }}>
          {items.length === 0 ? (
            <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', marginTop: 40 }}>
              Todavía no hay valoraciones del jurado registradas en esta categoría.
            </p>
          ) : (
            <>
          <p style={{ textAlign: 'center', color: 'var(--color-text-secondary)', marginBottom: 16 }}>
            Toca una candidatura de la lista para ver su puntuación y los comentarios del jurado.
          </p>

          <div className="jurado-split">
            <div className="jurado-list" role="list">
              {items.map((r, i) => {
                const score = r.average_score || 0;
                const isActive = i === itemIndex;
                const label = titleOf(r);
                return (
                  <button
                    key={r.id}
                    type="button"
                    role="listitem"
                    title={label}
                    className={`jurado-list-item${isActive ? ' active' : ''}`}
                    onClick={() => setItemIndex(i)}
                  >
                    <span className="jurado-list-rank">{i + 1}</span>
                    <span className="jurado-list-main">
                      <span className="jurado-list-name">{label}</span>
                      <span className="jurado-list-bar-track">
                        <span
                          className="jurado-list-bar-fill"
                          style={{ width: `${(score / maxScore) * 100}%` }}
                        />
                      </span>
                    </span>
                    <span className="jurado-list-score">{score.toFixed(2)}</span>
                  </button>
                );
              })}
            </div>

            <div className="jurado-detail">
              {item && (
                <article className="informe-ficha" style={{ marginBottom: 0 }}>
                  <div className="informe-ficha-head">
                    <span className="informe-ficha-num">CANDIDATURA {itemIndex + 1} DE {items.length}</span>
                    <span className="informe-status-pill st-approved">
                      Media: {(item.average_score || 0).toFixed(2)}
                    </span>
                  </div>
                  <h4 className="informe-ficha-title">{titleOf(item)}</h4>

                  <div className="informe-ficha-body">
                    <div className="informe-ficha-field">
                      <span className="informe-flabel">Comentarios del jurado</span>
                      {!item.comments || item.comments.length === 0 ? (
                        <p style={{ fontStyle: 'italic' }}>Todavía no hay comentarios registrados.</p>
                      ) : (
                        <div className="jurado-comment-carousel">
                          <div className="jurado-comment-nav">
                            <span className="jurado-comment-count">
                              Comentario {commentIndex + 1} de {item.comments.length}
                            </span>
                            {item.comments.length > 1 && (
                              <div style={{ display: 'flex', gap: 6 }}>
                                <button
                                  type="button"
                                  className="jurado-comment-nav-btn"
                                  onClick={() => setCommentIndex((i) => Math.max(0, i - 1))}
                                  disabled={commentIndex === 0}
                                  aria-label="Comentario anterior"
                                >
                                  <ChevronLeft size={16} />
                                </button>
                                <button
                                  type="button"
                                  className="jurado-comment-nav-btn"
                                  onClick={() =>
                                    setCommentIndex((i) => Math.min((item.comments?.length || 1) - 1, i + 1))
                                  }
                                  disabled={commentIndex === item.comments.length - 1}
                                  aria-label="Siguiente comentario"
                                >
                                  <ChevronRight size={16} />
                                </button>
                              </div>
                            )}
                          </div>

                          <p style={{ margin: '0 0 4px', fontWeight: 700, color: '#ffd702', fontSize: 13.5 }}>
                            {item.comments[commentIndex]?.juror_name || 'Jurado'}
                          </p>
                          <p style={{ margin: 0 }}>{item.comments[commentIndex]?.comment}</p>

                          {item.comments.length > 1 && (
                            <div className="jurado-comment-dots">
                              {item.comments.map((_, i) => (
                                <button
                                  key={i}
                                  type="button"
                                  className={`jurado-comment-dot${i === commentIndex ? ' active' : ''}`}
                                  onClick={() => setCommentIndex(i)}
                                  aria-label={`Ver comentario ${i + 1}`}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              )}
            </div>
          </div>
            </>
          )}
        </div>
      </div>
    );
  }

  /* -------------------------------------------------------------- Inicio */
  return (
    <div className="informe-page">
      <SeoMeta
        title="Valoración del Jurado · Premios NovaGob a la Innovación Pública 2026"
        description="Consulta las puntuaciones y comentarios del jurado experto en innovación pública sobre las candidaturas a los Premios NovaGob Excelencia 2026, categoría a categoría."
        path="/valoracion-jurado"
      />
      <header className="informe-page-header">
        <Link to="/" className="informe-page-back">
          <ArrowLeft size={18} /> Volver
        </Link>
        <span className="informe-page-title">Valoración del jurado · Edición 2026</span>
        <Link to="/" className="informe-page-home">
          Volver al inicio
        </Link>
      </header>

      <div className="informe-page-content">
        <section className="bases-modal-section">
          <h3>¿Qué categoría quieres consultar?</h3>
          <p style={{ marginBottom: 18 }}>
            Elige una categoría para descubrir a sus 3 finalistas, la puntuación de cada candidatura y los
            comentarios del jurado.
          </p>
          <div className="informe-catgrid jurado-catgrid" style={{ ['--cat-rows' as any]: catGridRows }}>
            {categoryStats.map((c) => {
              const top3 = (grouped[c.name] || []).slice(0, 3);
              return (
                <button
                  key={c.name}
                  type="button"
                  className="informe-catcard jurado-catcard"
                  onClick={() => openCategory(c.name)}
                >
                  <span className="jurado-catcard-header">
                    <span className="informe-catcard-dot" style={{ background: TYPE_DOT[c.type] }} />
                    <span className="informe-catcard-name">{c.name}</span>
                    <span className="informe-catcard-count">{c.count}</span>
                    <ChevronRight size={16} className="informe-catcard-arrow" />
                  </span>

                  {top3.length === 0 ? (
                    <span className="jurado-catcard-empty">Todavía sin valoraciones del jurado</span>
                  ) : (
                    <span className="jurado-catcard-finalists">
                      <span className="jurado-catcard-finalists-label">
                        <Trophy size={12} /> Finalistas
                      </span>
                      {top3.map((r, i) => (
                        <span className={`jurado-catcard-frow rank-${i + 1}`} key={r.id}>
                          <span className="jurado-catcard-fbadge">{i + 1}</span>
                          <span className="jurado-catcard-fname">{titleOf(r)}</span>
                          <span className="jurado-catcard-fscore">{(r.average_score || 0).toFixed(2)}</span>
                        </span>
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </section>

        <div className="bases-modal-footer">
          <p className="bases-modal-footer-credits">
            Documento de uso interno · Las puntuaciones reflejan la valoración individual de cada persona del
            jurado y no constituyen un resultado final ni definitivo.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ValoracionJurado;
